'use client';

import { FC } from 'react';
import { useTranslations } from 'next-intl';
import { getListingProviderBrands, getListingProviderDisplayName } from '@/hotdesk-ui/utils/provider';
import styles from './ProviderLabel.module.scss';
export interface ProviderLabelProps {
  listing?: unknown;
  /**
   * Override the resolved label when caller already knows it. When set,
   * a single chip is rendered with this string verbatim — multi-brand
   * splitting only kicks in when reading off the listing.
   */
  provider?: string | null;
  variant?: 'solid' | 'subtle' | 'outline';
  className?: string;
  /** When true, renders nothing for non-provider listings. Default true. */
  onlyWhenProvider?: boolean;
}

/**
 * Renders one chip per IWG marketing brand (`metaData.iwgBrands`).
 *
 * - Single-brand site → one chip ("Regus").
 * - Multi-brand co-located site → one chip per brand ("Regus" + "Spaces").
 * - Stale data with `provider: "iwg"` but no `iwgBrands` → single fallback
 *   chip showing the curated provider name ("IWG").
 * - Native (non-provider) listings → renders nothing.
 *
 * Per BE spec `docs/IWG/frontend_task_iwg_brands_label.md`.
 */
const ProviderLabel: FC<ProviderLabelProps> = ({
  listing,
  provider,
  variant = 'solid',
  className,
  onlyWhenProvider = true
}) => {
  const t = useTranslations();
  const variantClass = variant === 'subtle' ? styles.subtle : variant === 'outline' ? styles.outline : '';

  // Explicit override → single chip, caller's string.
  if (provider !== undefined) {
    if (!provider && onlyWhenProvider) return null;
    return <span className={styles.group}>
                <span className={`${styles.badge} ${variantClass} ${className ?? ''}`.trim()} aria-label={t('booking.providerBadge', {
        provider: provider ?? ''
      })}>
                    {provider}
                </span>
            </span>;
  }
  const brands = getListingProviderBrands(listing);
  if (brands.length > 0) {
    return <span className={styles.group}>
                {brands.map(brand => <span key={brand} className={`${styles.badge} ${variantClass} ${className ?? ''}`.trim()} aria-label={t('booking.providerBadge', {
        provider: brand
      })}>
                        {brand}
                    </span>)}
            </span>;
  }

  // Fallback: a listing flagged as provider-backed but missing iwgBrands
  // (e.g. a workspace not yet re-synced). Render the curated provider
  // name with the legacy "By {name}" pattern so we don't show "iwg".
  const fallback = getListingProviderDisplayName(listing);
  if (!fallback) return onlyWhenProvider ? null : null;
  return <span className={styles.group} data-sentry-component="ProviderLabel" data-sentry-source-file="ProviderLabel.tsx">
            <span className={`${styles.badge} ${variantClass} ${className ?? ''}`.trim()} aria-label={t('booking.providerBadge', {
      provider: fallback
    })}>
                <span className={styles.prefix}>{t('booking.providerBy')}</span>
                {fallback}
            </span>
        </span>;
};
export default ProviderLabel;